export interface CatalogService {
  name: string
  url?: string
}

// The service catalog entries that can point at an on-host Glance.
// loadServiceCatalog renames the single moving admin endpoint to 'glance-admin'
// and sets the 'glance-cluster' url to every image-library host's endpoint.
const GLANCE_ENTRIES = ['glance-cluster', 'glance-admin']

/**
 * The interface line the upload snippet always needs.
 *
 * Image data is stored on the image library host itself, not on the control
 * plane, so an upload has to go straight to that host. Only the admin interface
 * resolves Glance to it.
 */
// GNP-FR-004: the snippet always sets the admin interface.
export const UPLOAD_INTERFACE_LINE = 'export OS_INTERFACE=admin'

function hostFromUrl(raw: string): string {
  try {
    const { hostname } = new URL(raw.trim())
    return hostname
  } catch {
    return ''
  }
}

/**
 * Hosts of every on-host Glance endpoint, for no_proxy. Image upload must use
 * OS_INTERFACE=admin, and that endpoint is a bare host address which a
 * corporate proxy usually cannot reach.
 */
// GNP-FR-001: every glance-cluster and glance-admin endpoint host, in order, deduped.
// GNP-FR-002: unparseable or missing entries are skipped rather than thrown on.
export function glanceNoProxyHosts(services: CatalogService[] | undefined): string[] {
  const hosts: string[] = []
  for (const name of GLANCE_ENTRIES) {
    const entry = (services || []).find((s) => s?.name === name)
    for (const part of (entry?.url || '').split(',')) {
      const host = hostFromUrl(part)
      if (host && !hosts.includes(host)) hosts.push(host)
    }
  }
  return hosts
}

/**
 * The proxy-bypass block, or '' when the catalog names no image-library host.
 *
 * Only the image-library hosts go in. A proxy already has a rule for the
 * control plane, because that is the address every ordinary command uses; what
 * it has no rule for is these hosts, because nothing ever told the operator
 * they exist.
 *
 * The bypass list has two spellings and they are not interchangeable. Python's
 * requests reads the lower case `no_proxy` first and ignores `NO_PROXY`
 * entirely once it is set, so writing only the lower case name on a machine
 * whose list lives in `NO_PROXY` would silently drop every address the
 * operator was already bypassing. Seed from whichever is set, then write both.
 */
// GNP-FR-003: the no_proxy lines, or '' when there is nothing to add.
export function glanceNoProxyExport(services: CatalogService[] | undefined): string {
  const hosts = glanceNoProxyHosts(services)
  if (!hosts.length) return ''
  return [
    // GNP-FR-005: seed from either spelling, extend it, then export both.
    // biome-ignore lint/suspicious/noTemplateCurlyInString: shell expansion emitted verbatim, not a JS template
    'no_proxy=${no_proxy:-$NO_PROXY}',
    `export no_proxy=\${no_proxy:+$no_proxy,}${hosts.join(',')}`,
    'export NO_PROXY=$no_proxy',
  ].join('\n')
}
